import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { ITitle } from '@interfaces/common/common';

import { TVendorsValues } from '@enums/common';

import { columnSizes } from '@constants/table';
import { columnHeaders, MAX_USERS_COLUMN_WIDTH } from './constants';

import { renderSeverityCell, renderTitleOrNameCell } from './columnUtils';

interface IVendorColumnsProps {
	vendor: TVendorsValues;
	openEditAlarmForm: (alarm: IAlarmManagement) => void;
}

type TAlarmManagementRow = IAlarmManagement;

export const getAlarmManagementColumns = ({ vendor, openEditAlarmForm }: IVendorColumnsProps) => {
	return ([
		{
			header: 'alarm id',
			accessorKey: columnHeaders.alarmId?.[vendor] || columnHeaders.alarmId.LIMENET,
			isHidden: !columnHeaders.alarmId?.[vendor],
			size: columnSizes.size115,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'alarm name',
			accessorKey: columnHeaders.alarmName?.[vendor] || columnHeaders.alarmName.LIMENET,
			isHidden: !columnHeaders.alarmName?.[vendor],
			size: columnSizes.size215,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'group',
			accessorKey: columnHeaders.group?.[vendor] || columnHeaders.group.AWTG_ORAN,
			isHidden: !columnHeaders.group?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as ITitle | undefined;
				const { title = '' } = value || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'groups',
			accessorKey: columnHeaders.groups?.[vendor] || columnHeaders.groups.LIMENET,
			isHidden: !columnHeaders.groups?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as ITitle[] | undefined;
				const groups = value?.map(({ title }: ITitle) => title).join(', ');

				return <AdaptiveTableCell text={groups} />;
			},
		},
		{
			header: 'module',
			accessorKey: columnHeaders.moduleName?.[vendor] || columnHeaders.moduleName.AWTG_ORAN,
			isHidden: !columnHeaders.moduleName?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as ITitle | undefined;
				const { title = '' } = value || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'roles',
			accessorKey: columnHeaders.roles?.[vendor] || columnHeaders.roles.LIMENET,
			isHidden: !columnHeaders.roles?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as ITitle[] | undefined;
				const roles: string[] | undefined = value?.map(({ title }: ITitle) => title);
				const rolesTooltip = roles?.length
					? <div>{roles.map(title => <div key={title}>{title}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={roles?.join(', ')}
						tooltipText={rolesTooltip}
					/>
				);
			},
		},
		{
			header: 'users',
			accessorKey: columnHeaders.users?.[vendor] || columnHeaders.users.LIMENET,
			isHidden: !columnHeaders.users?.[vendor],
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as Array<{ fullName: string }> | undefined;
				const users: string[] | undefined = value?.map((v: { fullName: string }) => v?.fullName);
				const usersTooltip = users?.length
					? <div>{users.map((fullName, index) => <div key={`${fullName}${index}`}>{fullName}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={users?.join(', ')}
						tooltipText={usersTooltip}
					/>
				);
			},
		},
		{
			header: 'status',
			accessorKey: columnHeaders.status?.[vendor] || columnHeaders.status.LIMENET,
			isHidden: !columnHeaders.status?.[vendor],
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as { title?: string } | boolean | undefined;
				const title = typeof value === 'object' && value?.title
					? value.title
					: value
						? 'Enable'
						: 'Disable';

				return (
					<AdaptiveTableCell
						maxTextWidth={MAX_USERS_COLUMN_WIDTH}
						text={title}
					/>
				);
			},
		},
		{
			header: 'severity',
			accessorKey: columnHeaders.severity?.[vendor] || columnHeaders.severity.LIMENET,
			isHidden: !columnHeaders.severity?.[vendor],
			size: columnSizes.size85,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderSeverityCell(getValue),
		},
		{
			header: 'applicability',
			accessorKey: columnHeaders.applicability?.[vendor] || columnHeaders.applicability.AWTG_ORAN,
			isHidden: !columnHeaders.applicability?.[vendor],
			size: columnSizes.size215,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as ITitle | undefined;
				const { title = '' } = value || {};

				return <AdaptiveTableCell text={title} />;
			},
		},
		{
			header: 'probable cause',
			accessorKey: (columnHeaders.probableCause?.[vendor] || columnHeaders.probableCause.AWTG_ORAN) as string,
			isHidden: !columnHeaders.probableCause?.[vendor],
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => (
				<AdaptiveTableCell text={getValue() as string} />
			),
		},
		{
			header: 'trigger condition',
			accessorKey: columnHeaders.triggerCondition?.[vendor] || columnHeaders.triggerCondition.LIMENET,
			isHidden: true,
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'notes',
			accessorKey: columnHeaders.notes?.[vendor] || columnHeaders.notes.LIMENET,
			isHidden: !columnHeaders.notes?.[vendor],
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'alarm code',
			accessorKey: columnHeaders.alarmCode?.[vendor] || columnHeaders.alarmCode.FLEXI_DAS,
			isHidden: !columnHeaders.alarmCode?.[vendor],
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'node type',
			accessorKey: columnHeaders.nodeType?.[vendor] || columnHeaders.nodeType.FLEXI_DAS,
			isHidden: !columnHeaders.nodeType?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const value = getValue() as { name?: string } | undefined;
				const { name = '' } = value || {};

				return <AdaptiveTableCell text={name} />;
			},
		},
		{
			header: 'masked',
			accessorKey: columnHeaders.masked?.[vendor] || columnHeaders.masked.FLEXI_DAS,
			isHidden: !columnHeaders.masked?.[vendor],
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => {
				const status = getValue() ? 'Enable' : 'Disable';

				return <AdaptiveTableCell text={status} />;
			},
		},
		{
			header: 'hysteresis',
			accessorKey: columnHeaders.hysteresis?.[vendor] || columnHeaders.hysteresis.FLEXI_DAS,
			isHidden: !columnHeaders.hysteresis?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'description',
			accessorKey: columnHeaders.description?.[vendor] || columnHeaders.description.FLEXI_DAS,
			isHidden: !columnHeaders.description?.[vendor],
			enableSorting: false,
			cell: ({ getValue }: CellContext<TAlarmManagementRow, unknown>) => renderTitleOrNameCell(getValue),
		},
		{
			header: 'actions',
			accessorKey: 'actions',
			isHidden: !columnHeaders.actions?.[vendor],
			size: columnSizes.size80,
			enableSorting: false,
			cell: ({ row }: CellContext<TAlarmManagementRow, unknown>) => {
				const handleEdit = () => openEditAlarmForm(row?.original);

				return (
					<Actions
						editHandler={handleEdit}
						editTooltip='Edit details'
						gridNumberOfColumns={1}
						isEdit
					/>
				);
			},
		},
	]);
};
